import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Clock, Loader2, Save } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'

/**
 * Admin → Settings → Online Ordering → Business hours.
 *
 * Writes the `storefront_hours` setting. The backend computes open/closed
 * server-side (fixed PKT offset — Pakistan has no DST) and blocks online
 * pickup/delivery orders outside the window. Dine-in (QR) is exempt.
 */
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

interface DayWindow {
  closed: boolean
  open: string
  close: string
}

function normalizeDays(raw: unknown): DayWindow[] {
  const arr = Array.isArray(raw) ? raw : []
  return Array.from({ length: 7 }, (_, i) => {
    const d = arr[i] as Partial<DayWindow> | undefined
    return {
      closed: !!d?.closed,
      open: typeof d?.open === 'string' && d.open ? d.open : '09:00',
      close: typeof d?.close === 'string' && d.close ? d.close : '22:00',
    }
  })
}

export function AdminStorefrontHours() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const settingsQuery = useQuery({ queryKey: ['settings', 'all'], queryFn: () => apiClient.getAllSettings() })
  const d = settingsQuery.data?.data as Record<string, unknown> | undefined

  const [enabled, setEnabled] = useState(false)
  const [days, setDays] = useState<DayWindow[]>(() => normalizeDays(null))

  useEffect(() => {
    if (!d) return
    const saved = d.storefront_hours as { enabled?: boolean; days?: unknown } | undefined
    setEnabled(!!saved?.enabled)
    setDays(normalizeDays(saved?.days))
  }, [d])

  const setDay = (i: number, patch: Partial<DayWindow>) =>
    setDays((prev) => prev.map((day, idx) => (idx === i ? { ...day, ...patch } : day)))

  const saveMutation = useMutation({
    mutationFn: () => apiClient.updateSetting('storefront_hours', { enabled, offset_minutes: 300, days }),
    onSuccess: () => {
      toast({ title: 'Business hours saved' })
      queryClient.invalidateQueries({ queryKey: ['settings', 'all'] })
    },
    onError: (err) =>
      toast({ title: 'Could not save', description: err instanceof Error ? err.message : String(err), variant: 'destructive' }),
  })

  return (
    <Card className="space-y-5 p-5">
      <div className="flex items-center gap-3 border-b border-border/60 pb-3">
        <Clock className="h-5 w-5 text-primary" aria-hidden />
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold">Business hours</h3>
          <p className="text-xs text-muted-foreground">
            When online ordering is open. Outside these hours customers see "Closed" and can't place pickup/delivery
            orders. Dine-in QR orders are unaffected. Times are local (Pakistan).
          </p>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} aria-label="Enable business hours" />
      </div>

      {enabled && (
        <div className="space-y-2">
          {days.map((day, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="w-10 text-sm font-medium">{DAY_LABELS[i]}</span>
              <Switch
                checked={!day.closed}
                onCheckedChange={(on) => setDay(i, { closed: !on })}
                aria-label={`${DAY_LABELS[i]} open`}
              />
              {day.closed ? (
                <span className="text-sm text-muted-foreground">Closed</span>
              ) : (
                <div className="flex items-center gap-2">
                  <Input
                    type="time"
                    value={day.open}
                    onChange={(e) => setDay(i, { open: e.target.value })}
                    className="w-32"
                  />
                  <span className="text-muted-foreground">–</span>
                  <Input
                    type="time"
                    value={day.close}
                    onChange={(e) => setDay(i, { close: e.target.value })}
                    className="w-32"
                  />
                </div>
              )}
            </div>
          ))}
          <p className="pt-1 text-[11px] text-muted-foreground">
            For an overnight window (e.g. 6 PM–2 AM) set the close time earlier than the open time.
          </p>
        </div>
      )}

      <div className="flex justify-end border-t border-border/60 pt-4">
        <Button type="button" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} className="gap-1.5">
          {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save hours
        </Button>
      </div>
    </Card>
  )
}
